"use client";
import React from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Container,
  Chip,
} from "@mui/material";
import Image from "next/image";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import "swiper/css";
import { IMAGES } from "../constants/images";
import { useTranslation } from "react-i18next";

const candidateImages = [
  IMAGES.CANDIDATE_1,
  IMAGES.CANDIDATE_2,
  IMAGES.CANDIDATE_3,
  IMAGES.CANDIDATE_4,
  IMAGES.CANDIDATE_5,
  IMAGES.CANDIDATE_6,
  IMAGES.CANDIDATE_7,
  IMAGES.CANDIDATE_8,
  IMAGES.CANDIDATE_9,
  IMAGES.CANDIDATE_10,
  IMAGES.CANDIDATE_11,
  IMAGES.CANDIDATE_12,
  IMAGES.CANDIDATE_13,
  IMAGES.CANDIDATE_14,
];

export default function CandidatesLoveZayn() {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  const candidatesRaw = t("candidatesLoveZayn.candidates", {
    returnObjects: true,
  });
  const candidatesData = Array.isArray(candidatesRaw) ? candidatesRaw : [];

  const candidates = candidatesData.map((c, i) => ({
    ...c,
    image: candidateImages[i],
    bgColor: "#fff",
  }));

  const loopCandidates = [...candidates, ...candidates];

  const CandidateCard = ({
    candidate,
    desktopFontSize = "19px",
    roleFontSize = "15px",
    quoteFontSize = "15px",
    mb = 2,
  }) => (
    <Card
      sx={{
        borderRadius: "20px",
        boxShadow: "none",
        border: "1px solid #f0f0f0",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        "&:hover": { boxShadow: "0 12px 24px rgba(0,0,0,0.08)" },
      }}
    >
      <Box
        sx={{
          position: "relative",
          paddingTop: "100%",
          bgcolor: candidate.bgColor || "#fff",
          overflow: "hidden",
        }}
      >
        <Image
          src={candidate.image}
          alt={candidate.name}
          fill
          style={{ objectFit: "contain" }}
        />
      </Box>
      <CardContent
        sx={{
          p: 3,
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          textAlign: isArabic ? "right" : "left",
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 700,
            fontSize: desktopFontSize,
            color: "#6B6D80",
            fontFamily: "var(--font-outfit)",
          }}
        >
          {candidate.name}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            mb,
            fontSize: roleFontSize,
            color: "#6B6D80",
            fontFamily: "var(--font-outfit)",
            fontWeight: 400,
          }}
        >
          {candidate.role}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            lineHeight: 1.4,
            color: "#6B6D80",
            fontFamily: "var(--font-outfit)",
            fontWeight: 400,
            fontSize: quoteFontSize,
          }}
        >
          {candidate.quote}
        </Typography>
      </CardContent>
    </Card>
  );

  return (
    <Box
      sx={{ bgcolor: "#ffffff", py: 1, direction: isArabic ? "rtl" : "ltr" }}
    >
      <Container
        sx={{
          width: { xs: "100%", lg: "calc(100% - 60px)" },
          ml: { xs: 0, lg: "40px" },
          mr: { xs: 0, lg: "40px" },
          maxWidth: "none !important",
          px: { xs: 2, lg: "0 !important" },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            mb: 2,
            ml: isArabic ? 0 : { xs: 0, lg: "2%" },
            mr: isArabic ? { xs: 0, lg: "2%" } : 0,
          }}
        >
          <Chip
            label={t("candidatesLoveZayn.chip")}
            sx={{
              backgroundColor: "transparent",
              border: "1px solid #DDE4E8",
              mb: 1,
              color: "#647482",
              fontSize: "14px",
              fontWeight: "400",
              px: 1,
              py: 2.3,
              borderRadius: "20px",
              fontFamily: "var(--font-plusjakarta)",
            }}
          />
        </Box>

        {/* Title + Description */}
        <Box
          sx={{
            mb: 6,
            ml: isArabic ? 0 : { xs: 0, lg: "2%" },
            mr: isArabic ? { xs: 0, lg: "2%" } : 0,
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              mb: 2,
              display: "flex",
              alignItems: "center",
              flexDirection: isArabic ? "row-reverse" : "row",
              justifyContent: isArabic ? "flex-end" : "flex-start",
              gap: 1,
              fontSize: {
                xs: "28px",
                sm: "32px",
                md: "37px",
                lg: "37px",
                xl: "43px",
              },
              color: "#000000",
              fontFamily: "var(--font-plusjakarta)",
            }}
          >
            {t("candidatesLoveZayn.title1")}{" "}
            <span style={{ color: "#ff6b6b" }}>❤️</span>{" "}
            {t("candidatesLoveZayn.title2")}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "#6B6D80",
              fontSize: {
                xs: "16px",
                sm: "18px",
                md: "18px",
                lg: "18px",
                xl: "20px",
              },
              lineHeight: 1.6,
              fontFamily: "var(--font-outfit)",
              textAlign: isArabic ? "right" : "left",
            }}
          >
            {t("candidatesLoveZayn.subtitle1")}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "#6B6D80",
              fontSize: { xs: "16px", sm: "18px", md: "20px" },
              lineHeight: 1.6,
              fontFamily: "var(--font-outfit)",
              textAlign: isArabic ? "right" : "left",
            }}
          >
            {t("candidatesLoveZayn.subtitle2")}
          </Typography>
        </Box>

        {/* MOBILE: Swiper */}
        <Box
          sx={{ display: { xs: "block", md: "none" }, mb: 4, direction: "ltr" }}
        >
          <Swiper
            modules={[Autoplay]}
            spaceBetween={16}
            slidesPerView={1}
            loop
            autoplay={{ delay: 2000, disableOnInteraction: false }}
            speed={800}
          >
            {candidates.map((candidate, index) => (
              <SwiperSlide key={index}>
                <Box sx={{ mx: 1.5 }}>
                  <CandidateCard
                    candidate={candidate}
                    desktopFontSize="18px"
                    roleFontSize="14px"
                    quoteFontSize="14px"
                    mb={1}
                  />
                </Box>
              </SwiperSlide>
            ))}
          </Swiper>
        </Box>

        {/* DESKTOP: Continuous Carousel */}
        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            overflow: "hidden",
            mb: 4,
            direction: "ltr",
          }}
        >
          <Box
            sx={{
              display: "flex",
              width: "200%",
              animation: "scrollCandidates 60s linear infinite",
              willChange: "transform",
              backfaceVisibility: "hidden",
              "&:hover": { animationPlayState: "paused" },
              "@keyframes scrollCandidates": {
                from: { transform: "translateX(0)" },
                to: { transform: "translateX(-120%)" },
              },
            }}
          >
            {loopCandidates.map((candidate, index) => (
              <Box
                key={index}
                sx={{
                  flex: {
                    xs: "0 0 50%",
                    sm: "0 0 50%",
                    md: "0 0 30%",
                    lg: "0 0 12%",
                    xl: "0 0 12%",
                  },
                  px: { xs: 1, sm: 1, md: 1.5 },
                  boxSizing: "border-box",
                }}
              >
                <CandidateCard candidate={candidate} />
              </Box>
            ))}
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
